import {
  AlertCircle,
  Building2, Button, CheckCircle,
  Clock,
  Image,
  Save,
  Settings as SettingsIcon,
  Stethoscope
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useManager from "../../hooks/useManager";
import {
  fetchFacilityRequest,
  updateFacilityRequest,
} from "../../store/reducers/facilitySlice";
import {
  selectFacility,
  selectFacilityUpdateError,
  selectFacilityUpdating,
} from "../../store/selectors/facilitySelector";
import { Facility, FacilityUpdate } from "../../types/facility.definition";
import {
  GeneralInfoTab,
  MediaTab,
  OperatingHoursTab,
  ServicesTab,
} from "./settings/tabs";
export type TabType = "general" | "hours" | "services" | "media";

export const FacilitySettings: React.FC = () => {
  const { t } = useTranslation();
  const manager = useManager();
  const dispatch = useDispatch();

  const storeFacility = useSelector(selectFacility);
  const isUpdating = useSelector(selectFacilityUpdating);
  const updateError = useSelector(selectFacilityUpdateError);

  const [facility, setFacility] = useState<Facility>(
    storeFacility || manager.healthcareFacility,
  );
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("general");
  const [animateCards, setAnimateCards] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const [wasUpdating, setWasUpdating] = useState(false);

  // Compute operating days from hours
  const computeOperatingDays = () => {
    if (!facility.hours || typeof facility.hours !== "object") return 0;

    return Object.values(facility.hours).filter((dayHours) => {
      return (
        dayHours && dayHours.open !== "Closed" && dayHours.close !== "Closed"
      );
    }).length;
  };

  const getOperatingDaysText = () => {
    const days = computeOperatingDays();
    if (days === 0) return t("facilitySettings.stats.noOperatingDays");
    if (days === 1) return t("facilitySettings.stats.oneDayPerWeek");
    return t("facilitySettings.stats.daysPerWeek", { count: days });
  };

  // Trigger animations after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Fetch the latest facility data on mount via the store
  useEffect(() => {
    if (manager.healthcareFacility?.id) {
      dispatch(
        fetchFacilityRequest({ facilityId: manager.healthcareFacility.id }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local state when store facility changes
  useEffect(() => {
    if (storeFacility) {
      setFacility(storeFacility);
    }
  }, [storeFacility]);

  // Handle update completion (success or error)
  useEffect(() => {
    if (wasUpdating && !isUpdating) {
      if (updateError) {
        setErrorMessage(updateError);
        setTimeout(() => setErrorMessage(""), 5000);
      } else {
        setSuccessMessage(t("facilitySettings.successMessage"));
        requestAnimationFrame(() => {
          const successElement = document.getElementById("success-message");
          if (successElement) {
            successElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        setTimeout(() => setSuccessMessage(""), 3000);
      }
      setWasUpdating(false);
    }
  }, [isUpdating, wasUpdating, updateError, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    if (facility.id) {
      const original = storeFacility || manager.healthcareFacility;

      // Build diff payload — only include changed fields
      const updatePayload: Partial<FacilityUpdate> = {};

      if (facility.name?.trim() !== original.name?.trim()) {
        updatePayload.name = facility.name?.trim();
      }
      if (facility.contactEmail?.trim() !== original.contactEmail?.trim()) {
        updatePayload.contactEmail = facility.contactEmail?.trim();
      }
      if (facility.phoneNumber?.trim() !== original.phoneNumber?.trim()) {
        updatePayload.phoneNumber = facility.phoneNumber?.trim();
      }
      if (facility.website?.trim() !== original.website?.trim()) {
        updatePayload.website = facility.website?.trim();
      }
      if (facility.facilityType !== original.facilityType) {
        updatePayload.facilityType = facility.facilityType;
      }
      if (
        JSON.stringify(facility.location) !== JSON.stringify(original.location)
      ) {
        updatePayload.location = facility.location;
      }
      if (
        JSON.stringify(facility.specialties) !==
        JSON.stringify(original.specialties)
      ) {
        updatePayload.specialtyIds = facility.specialties
          ?.filter((s) => s)
          .map((s) => {
            if (typeof s === "string") {
              const num = Number(s);
              return isNaN(num) ? s : num;
            }
            if (typeof s === "object" && s !== null) {
              const specialtyObj = s as {
                id?: string;
                code?: string;
                label?: string;
              };
              const id = specialtyObj.id || specialtyObj.code || "";
              const num = Number(id);
              return isNaN(num) ? id : num;
            }
            return s;
          })
          .filter((id) => id);
      }
      if (
        JSON.stringify(facility.careTypes) !==
        JSON.stringify(original.careTypes)
      ) {
        updatePayload.careTypeIds = facility.careTypes
          ? facility.careTypes
            .filter(
              (ct) =>
                ct.id !== undefined &&
                ct.id !== null &&
                !isNaN(Number(ct.id)),
            )
            .map((ct) => Number(ct.id))
          : [];
      }
      if (
        JSON.stringify(facility.services) !== JSON.stringify(original.services)
      ) {
        updatePayload.services = facility.services;
      }
      if (
        JSON.stringify(facility.insurance) !==
        JSON.stringify(original.insurance)
      ) {
        updatePayload.insurance = facility.insurance;
      }
      if (JSON.stringify(facility.hours) !== JSON.stringify(original.hours)) {
        updatePayload.hours = facility.hours
          ? Object.entries(facility.hours)
            .filter(
              ([_, dayHours]) =>
                dayHours &&
                dayHours.open !== undefined &&
                dayHours.open !== null &&
                dayHours.close !== undefined &&
                dayHours.close !== null,
            )
            .map(([day, dayHours]) => ({ [day]: dayHours }))
          : [];
      }

      if (facility.aboutUs?.trim() !== original.aboutUs?.trim()) {
        updatePayload.aboutUs = facility.aboutUs?.trim() || undefined;
      }
      if (
        JSON.stringify(facility.social) !== JSON.stringify(original.social)
      ) {
        updatePayload.social = facility.social;
      }

      setWasUpdating(true);
      dispatch(
        updateFacilityRequest({
          facilityId: Number(facility.id),
          data: updatePayload,
        }),
      );
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <GeneralInfoTab
            facility={facility}
            setFacility={setFacility}
            onValidationChange={setIsFormValid}
          />
        );
      case "hours":
        return (
          <OperatingHoursTab facility={facility} setFacility={setFacility} />
        );
      case "services":
        return <ServicesTab facility={facility} setFacility={setFacility} />;
      case "media":
        return <MediaTab facility={facility} />;
      default:
        return null;
    }
  };

  const tabs = [
    {
      id: "general" as TabType,
      label: t("facilitySettings.tabs.general.label"),
      icon: Building2,
      description: t("facilitySettings.tabs.general.description"),
      color: "bg-primary-500",
      delay: 100,
    },
    {
      id: "hours" as TabType,
      label: t("facilitySettings.tabs.hours.label"),
      icon: Clock,
      description: t("facilitySettings.tabs.hours.description"),
      color: "bg-success-500",
      delay: 200,
    },
    {
      id: "services" as TabType,
      label: t("facilitySettings.tabs.services.label"),
      icon: Stethoscope,
      description: t("facilitySettings.tabs.services.description"),
      color: "bg-secondary-700",
      delay: 300,
    },
    {
      id: "media" as TabType,
      label: t("facilityMedia.tabTitle"),
      icon: Image,
      description: t("facilityMedia.thumbnail.title"),
      color: "bg-warning-500",
      delay: 400,
    },
  ];

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div
        className={`transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="flex items-center mb-4 space-x-4">
          <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-ews-primary-100">
            <SettingsIcon className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">
              {t("facilitySettings.title")}
            </h1>
            <p className="text-neutral-600">{t("facilitySettings.subtitle")}</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div
          className={`p-4 rounded-xl border border-error-200 bg-error-50 transition-all duration-500 ${animateCards
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
            }`}
          style={{ transitionDelay: "200ms" }}
        >
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-error-600" />
            <span className="text-sm font-medium text-error-800">
              {errorMessage}
            </span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div
          id="success-message"
          className={`p-4 rounded-xl border border-success-200 bg-success-50 transition-all duration-500 ${animateCards
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
            }`}
          style={{ transitionDelay: "200ms" }}
        >
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-success-600" />
            <span className="text-sm font-medium text-success-800">
              {successMessage}
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`bg-white rounded-xl shadow-soft border border-neutral-200 overflow-hidden transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        style={{ transitionDelay: "300ms" }}
      >
        {/* Enhanced Tab Navigation */}
        <div className="border-b bg-neutral-50 border-neutral-200">
          <div className="flex flex-wrap">
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                delay={tab.delay}
                animateCards={animateCards}
              />
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-8">
            <div
              className={`transition-all duration-500 ${animateCards
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
                }`}
              style={{ transitionDelay: "500ms" }}
            >
              {renderTabContent()}
            </div>
          </div>

          {/* Enhanced Footer */}
          <div className="px-8 py-6 bg-gradient-to-r border-t from-neutral-50 to-neutral-100 border-neutral-200">
            <div className="flex flex-col justify-between items-center space-y-4 sm:flex-row sm:space-y-0">
              <div></div>
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={isUpdating || !isFormValid}
                  className="flex items-center hover-scale"
                >
                  {isUpdating ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                      <span>{t("facilitySettings.saving")}</span>
                    </div>
                  ) : (
                    <>
                      <Save className="mr-2 w-4 h-4" />
                      {t("facilitySettings.saveChanges")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Quick Stats */}
      <div
        className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        style={{ transitionDelay: "600ms" }}
      >
        <div className="p-6 bg-white rounded-xl border transition-all duration-300 shadow-soft border-neutral-200 hover:shadow-lg hover-lift">
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-ews-primary-100">
              <Building2 className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600">
                {t("facilitySettings.stats.facilityName")}
              </p>
              <p className="text-lg font-semibold text-neutral-900">
                {facility.name}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 bg-white rounded-xl border transition-all duration-300 shadow-soft border-neutral-200 hover:shadow-lg hover-lift">
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-success-100">
              <Clock className="w-5 h-5 text-success-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600">
                {t("facilitySettings.stats.operatingDays")}
              </p>
              <p className="text-lg font-semibold text-neutral-900">
                {getOperatingDaysText()}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 bg-white rounded-xl border transition-all duration-300 shadow-soft border-neutral-200 hover:shadow-lg hover-lift">
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-ews-secondary-100">
              <Stethoscope className="w-5 h-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600">
                {t("facilitySettings.stats.specialties")}
              </p>
              <p className="text-lg font-semibold text-neutral-900">
                {facility.specialties?.length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TabButtonProps {
  tab: {
    id: TabType;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
    color: string;
    delay: number;
  };
  isActive: boolean;
  onClick: () => void;
  delay: number;
  animateCards: boolean;
}

const TabButton: React.FC<TabButtonProps> = ({
  tab,
  isActive,
  onClick,
  delay,
  animateCards,
}) => {
  const IconComponent = tab.icon;

  return (
    <button
      className={`flex-1 px-6 py-4 text-left transition-all duration-300 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        } ${isActive
          ? "bg-white border-b-2 shadow-sm border-primary-500 text-primary-700"
          : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
      onClick={onClick}
      type="button"
    >
      <div className="flex items-center space-x-3">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${tab.color}`}
        >
          <IconComponent className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-sm font-medium">{tab.label}</p>
          <p className="hidden text-xs text-neutral-500 sm:block">
            {tab.description}
          </p>
        </div>
      </div>
    </button>
  );
};
